import { applyTownResidentSnapshots, townResidentServerTime } from '@/modules/game-session/town-residents';
import type { TownResidentSnapshot } from '@/modules/game-domain/world.types';
import { WorldEventNpcEntrance, nearestRoadDirection } from './world-event-npc-entrance';
import { NearbyHeroAnimations } from '@/modules/game-renderer/nearby-hero-animations';
import type { NearbyHeroPresentation } from '@/modules/game-domain/render-snapshot';
import { GamePhase } from './types';
import { HeroAnimationController } from '@/modules/game-renderer/hero-animation';
import { MAX_ACCUMULATED_MS } from '../shared/constants';
import type {
  GameState,
  EnemyState,
  HeroState,
  LootDrop,
  TownData,
  NearbyHeroData,
  NPCData,
  BuildingData,
  DebuffType,
  WorldEventNPCInfo,
} from './types';
import { applyServerStateEvent, initialGameState, type ServerStateEvent } from '@/modules/game-domain/server-state';
import { GameRenderer, worldToScreen, screenToWorld } from './renderer';
import { apiGet } from '@/network/api-client';
import { SceneryStream } from '@/modules/game-session/scenery-stream';
import type { SceneryChunk } from '@/modules/game-protocol/world-scenery';
import { townTerrainContext } from '@/modules/game-procedural/town-terrain-context';
import { Camera } from './camera';
import { clampGameZoom, fitTownCamera, MIN_GAME_ZOOM, type CameraControlsSnapshot } from './town-camera';
import { MovementTimeline } from '@/modules/game-domain/movement-presentation';
import type { MovementPlan } from '@/modules/game-protocol/ws-payloads.types';
import { createRenderSnapshot, type SpeechBubbleView } from '@/modules/game-domain/render-snapshot';
import { getViewport } from '../shared/telegram';
import { isWorldPointInViewport } from './viewport-bounds';

/** Standing idle is presentation-only and follows the server's native rest context. */
function isTownRestIdle(state: Readonly<GameState>): boolean {
  const hero = state.hero;
  if (!hero || hero.hp <= 0) return false;
  return (state.phase === GamePhase.Resting && hero.restKind?.toLowerCase() === 'town')
    || (state.phase === GamePhase.InTown && hero.excursionKind === 'town' && hero.townTourPhase?.toLowerCase() === 'rest');
}

/**
 * Server-authoritative game engine.
 *
 * This engine does NOT simulate walking, combat, encounters, or any
 * game logic. All state comes from the server via WebSocket messages.
 *
 * Responsibilities:
 *   - Hold current game state (hero, enemy, phase)
 *   - Interpolate hero position between server updates (2 Hz -> 60 fps)
 *   - Run the requestAnimationFrame render loop
 *   - Camera follow + shake
 *   - Emit state change and damage callbacks for React UI
 */

/** Drift threshold in world tiles before snapping to server position. */
const POSITION_DRIFT_SNAP_THRESHOLD = 2.0;

/** Interpolation window matching the server's 2 Hz send rate. */
const MOVE_UPDATE_INTERVAL_S = 0.5;

interface NearbyHeroInterp {
  displayX: number;
  displayY: number;
  prevX: number;
  prevY: number;
  targetX: number;
  targetY: number;
  lastUpdateTime: number;
  movingUntilMs: number;
}

export interface GameEngineOptions {
  authoritativeScenery?: boolean;
  /** A session owns the transport; GameEngine owns chunk scheduling and cancellation. */
  sceneryLoader?: (chunkX: number, chunkY: number, signal: AbortSignal) => Promise<SceneryChunk>;
  /** Local engine tools may control the public camera without automatic hero tracking. */
  cameraFollow?: boolean;
}

export class GameEngine {
  renderer: GameRenderer;
  camera: Camera;

  private _running = false;
  private _destroyed = false;
  private _rafId: number | null = null;
  private _lastTime = 0;

  /** Current game state (exposed to React via onStateChange) */
  private _gameState: GameState = initialGameState();

  // ---- Server-driven position interpolation ----
  private _heroAnimation = new HeroAnimationController();
  private _nearbyAnimations = new NearbyHeroAnimations();
  private _movement = new MovementTimeline();
  private _heroDisplayX = 0;
  private _heroDisplayY = 0;
  private _prevPositionX = 0;
  private _prevPositionY = 0;
  private _targetPositionX = 0;
  private _targetPositionY = 0;
  _moveTargetX = 0;
  _moveTargetY = 0;
  _heroSpeed = 0;
  private _heroStopFacing: {x:number;y:number}|undefined;
  private _lastMoveUpdateTime = 0;

  /** Road waypoints from route_assigned, for optional path rendering. */
  _routeWaypoints: Array<{ x: number; y: number }> = [];
  /** Full road graph polylines from init / GET /roads. */
  private _roadNetwork: Array<Array<{ x: number; y: number }>> = [];
  private _sceneryChunks: readonly SceneryChunk[] = [];
  private readonly _scenery: SceneryStream;
  private readonly _authoritativeScenery: boolean;
  private readonly _cameraFollow: boolean;

  /** Loot popup auto-clear timer */
  private _lootTimerMs = 0;

  /** Towns for map rendering */
  private _towns: TownData[] = [];
  private _residentServerTimeMs = 0;
  /** Presentation only: overview remains fixed while the hero walks through this town. */
  private _overviewTownId: number | null = null;
  private _cameraTownId: number | null = null;
  private _cameraGeometryRevision = 0;
  private _cameraControlsKey = '';
  private _cameraControlsSnapshot: CameraControlsSnapshot = { zoom: 1, minimum: MIN_GAME_ZOOM, inTown: false, overview: false };
  private _publishedCameraControlsSnapshot: CameraControlsSnapshot | null = null;
  private _cameraControlsListeners = new Set<() => void>();

  /** Stable cached projection for React; neither polls nor owns an animation loop. */
  getCameraControlsSnapshot = (): CameraControlsSnapshot => {
    const viewport = getViewport();
    const town = this.cameraTown;
    const key = `${this.camera.zoom}:${this._overviewTownId}:${town?.id}:${this._cameraGeometryRevision}:${viewport.width}:${viewport.height}`;
    if (key !== this._cameraControlsKey) {
      this._cameraControlsKey = key;
      this._cameraControlsSnapshot = {
        zoom: this.camera.zoom,
        minimum: town ? Math.min(MIN_GAME_ZOOM, fitTownCamera(town, viewport).zoom) : MIN_GAME_ZOOM,
        inTown: town !== undefined, overview: this._overviewTownId !== null,
      };
    }
    return this._cameraControlsSnapshot;
  };

  subscribeCameraControls = (listener: () => void): (() => void) => {
    this._cameraControlsListeners.add(listener);
    return () => { this._cameraControlsListeners.delete(listener); };
  };

  private _publishCameraControls(): void {
    if (!this._cameraControlsListeners.size) return;
    const snapshot = this.getCameraControlsSnapshot();
    if (snapshot === this._publishedCameraControlsSnapshot) return;
    this._publishedCameraControlsSnapshot = snapshot;
    for (const listener of this._cameraControlsListeners) listener();
  }

  get cameraTown(): TownData | undefined {
    if (this._gameState.phase !== GamePhase.InTown) return undefined;
    const id = this._cameraTownId ?? this._gameState.hero?.currentTownId;
    return this._towns.find(town => town.id === id && town.biome !== 'road');
  }

  get minimumCameraZoom(): number {
    const town = this.cameraTown;
    return town ? Math.min(MIN_GAME_ZOOM, fitTownCamera(town, getViewport()).zoom) : MIN_GAME_ZOOM;
  }

  get townOverviewActive(): boolean { return this._overviewTownId !== null; }

  setCameraZoom(zoom: number): void {
    this._overviewTownId = null;
    this.camera.zoom = clampGameZoom(zoom, this.minimumCameraZoom);
    const hero = worldToScreen(this._heroDisplayX, this._heroDisplayY);
    this.camera.setTarget(hero.x, hero.y);
    this.camera.snapToTarget();
    this._publishCameraControls();
  }

  showTownOverview(): boolean {
    const town = this.cameraTown;
    if (!town) return false;
    this._overviewTownId = town.id;
    this._updateCameraTarget();
    this.camera.snapToTarget();
    this._publishCameraControls();
    return true;
  }

  private _updateCameraTarget(): void {
    if (!this._cameraFollow) return;
    const town = this.cameraTown;
    if (this._overviewTownId !== null && town?.id === this._overviewTownId) {
      const fit = fitTownCamera(town, getViewport());
      this.camera.zoom = fit.zoom;
      this.camera.setTarget(fit.x, fit.y);
      return;
    }
    this._overviewTownId = null;
    if (this.camera.zoom < MIN_GAME_ZOOM) this.camera.zoom = Math.max(this.camera.zoom, this.minimumCameraZoom);
    if (this._gameState.hero) {
      const hero = worldToScreen(this._heroDisplayX, this._heroDisplayY);
      this.camera.setTarget(hero.x, hero.y);
    }
  }
  /** All NPCs from towns for rendering */
  private _allNPCs: NPCData[] = [];
  /** Nearby heroes from the shared world (polled periodically) */
  private _nearbyHeroes: NearbyHeroData[] = [];
  /** Per-hero display interpolation for nearby heroes (2 Hz -> 60 fps). */
  private _nearbyHeroInterp = new Map<number, NearbyHeroInterp>();

  /** Meet partner world snapshot (active session or linger ghost). */
  private _heroMeetOverlay: NearbyHeroData | null = null;
  /** Solo world-event NPC overlay (e.g. wandering merchant near hero). */
  private _worldEventNPCOverlay: {
    id: number;
    name: string;
    nameKey?: string;
    type: NPCData['type'];
  } | null = null;
  private _worldEventNpcEntrance = new WorldEventNpcEntrance();
  private _heroMeetLingerEndMs = 0;
  private _thoughtBubble: { text: string; key: string; args?: Record<string, unknown>; startMs: number; occurrenceId?: string } | null = null;
  private _crierBubble: { text: string; npcId: number; townId: number; startMs: number } | null = null;
  private _meetBubbleSelf: {
    text: string;
    startMs: number;
    player: boolean;
    /** Typed by local player (not auto/scripted); distinct balloon background on canvas */
    ownPlayerMessage: boolean;
  } | null = null;
  private _meetBubbleOther: {
    text: string;
    startMs: number;
    player: boolean;
    ownPlayerMessage: boolean;
  } | null = null;

  /** Debuff full-duration ms from last hero snapshot (`debuffCatalog`); used when WS omits durationMs. */
  private _debuffDurationMsByType: Partial<Record<DebuffType, number>> = {};

  /** Callbacks */
  private _onStateChange: ((state: GameState) => void) | null = null;
  private _handleResize: (() => void) | null = null;

  constructor(options: GameEngineOptions = {}) {
    this.renderer = new GameRenderer();
    this.camera = new Camera();
    this._authoritativeScenery = options.authoritativeScenery !== false;
    this._cameraFollow = options.cameraFollow !== false;
    this._scenery = new SceneryStream(
      options.sceneryLoader ?? ((x,y,signal) => apiGet<SceneryChunk>(`/world/scenery?chunkX=${x}&chunkY=${y}`,signal)),
      chunks => {
        if (this._destroyed) return;
        this._sceneryChunks = chunks;
        this.renderer.setAuthoritativeScenery(chunks);
      },
    );
  }

  get running(): boolean {
    return this._running;
  }

  get gameState(): GameState {
    return this._gameState;
  }

  // ---- Callback Registration ----

  /** Register a callback for game state changes (for React UI) */
  onStateChange(cb: (state: GameState) => void): void {
    this._onStateChange = cb;
  }

  /** Register a callback for damage events (floating numbers) */
  // ---- Data Setters (static data from REST init) ----

  /** Set the hero display name on the renderer label. */
  setHeroName(name: string): void {
    this.renderer.setHeroName(name);
  }

  /** Set towns for map rendering. */
  setTowns(towns: TownData[]): void {
    this._towns = towns;
    this._cameraGeometryRevision++;
    this.renderer.invalidateTowns();
    this._syncWorldTerrainContext();
  }

  /** Live resident paths update without rebuilding static terrain or geometry. */
  applyTownResidents(updates: readonly TownResidentSnapshot[]): void {
    this._towns = applyTownResidentSnapshots(this._towns, updates);
    this._residentServerTimeMs = townResidentServerTime(this._towns);
  }

  /** Set the full road network for procedural ground rendering. */
  setRoadNetwork(segments: Array<Array<{ x: number; y: number }>>): void {
    this._roadNetwork = segments;
    this._syncWorldTerrainContext();
  }

  /** Set NPC data for rendering. */
  setNPCs(npcs: NPCData[]): void {
    this._allNPCs = npcs;
  }

  /** Closest ordinary-town NPC within both server town and NPC access bounds. */
  getNearestTownNPC(): NPCData | null {
    if (!this._gameState.hero) return null;
    const accessibleTownIds = new Set(this._towns
      .filter((town) => town.biome !== 'road' && Math.hypot(
        this._heroDisplayX - town.centerX, this._heroDisplayY - town.centerY,
      ) <= town.radius)
      .map((town) => town.id));
    let nearest: NPCData | null = null;
    let nearestDistance = Infinity;
    for (const npc of this._allNPCs) {
      if (npc.interactive === false) continue;
      if (!accessibleTownIds.has(npc.townId)) continue;
      const distance = Math.hypot(this._heroDisplayX - npc.worldX, this._heroDisplayY - npc.worldY);
      // Match backend tuning.DefaultValues().NPCCostNearbyRadius (world units).
      if (distance <= 3 && distance < nearestDistance) {
        nearest = npc;
        nearestDistance = distance;
      }
    }
    return nearest;
  }

  /** Interpolated hero position in world space (for UI proximity checks). */
  getHeroDisplayWorldPosition(): { x: number; y: number } {
    return { x: this._heroDisplayX, y: this._heroDisplayY };
  }

  /** Update the list of nearby heroes for shared-world rendering. */
  setNearbyHeroes(heroes: NearbyHeroData[]): void {
    const myId = this._gameState.hero?.id;
    const filtered =
      myId != null ? heroes.filter((h) => h.id !== myId) : heroes;
    this._nearbyHeroes = filtered;
    this._nearbyAnimations.retain(new Set(filtered.map(hero => hero.id)));
    const seen = new Set<number>();
    for (const h of filtered) {
      seen.add(h.id);
      const existing = this._nearbyHeroInterp.get(h.id);
      if (!existing) {
        this._initNearbyHeroInterp(h.id, h.positionX, h.positionY);
        continue;
      }
      existing.displayX = h.positionX;
      existing.displayY = h.positionY;
      existing.prevX = h.positionX;
      existing.prevY = h.positionY;
      existing.targetX = h.positionX;
      existing.targetY = h.positionY;
      existing.lastUpdateTime = performance.now();
    }
    for (const id of this._nearbyHeroInterp.keys()) {
      if (!seen.has(id)) {
        this._nearbyHeroInterp.delete(id);
      }
    }
  }

  /** Apply a live movement update for a nearby hero. */
  applyNearbyHeroMove(
    heroId: number,
    x: number,
    y: number,
    targetX: number,
    targetY: number,
    speed: number,
  ): void {
    const idx = this._nearbyHeroes.findIndex((h) => h.id === heroId);
    if (idx >= 0) {
      const hero = this._nearbyHeroes[idx]!;
      this._nearbyHeroes[idx] = { ...hero, positionX: x, positionY: y };
    }

    this._applyNearbyHeroInterpStep(heroId, x, y);
    const interp = this._nearbyHeroInterp.get(heroId);
    if (interp) {
      const distance = Math.hypot(targetX - x, targetY - y);
      interp.movingUntilMs = distance > 0.05 && speed > 0
        ? performance.now() + distance / speed * 1000 + MOVE_UPDATE_INTERVAL_S * 1000
        : 0;
    }

    if (this._heroMeetOverlay?.id === heroId) {
      this._heroMeetOverlay = { ...this._heroMeetOverlay, positionX: x, positionY: y };
    }
  }

  /**
   * Start the world event partner overlay (e.g. paired hero meet).
   * Partner sprite is drawn next to the hero by the renderer.
   */
  setWorldEventPartnerOverlay(partner: NearbyHeroData): void {
    this._heroMeetOverlay = { ...partner };
    this._heroMeetLingerEndMs = 0;
    this._meetBubbleSelf = null;
    this._meetBubbleOther = null;
    this._initNearbyHeroInterp(partner.id, partner.positionX, partner.positionY);
  }

  /** End the partner overlay but keep it visible for lingerMs (perf ms). */
  endWorldEventPartnerOverlayLinger(lingerMs: number): void {
    const now = performance.now();
    this._heroMeetLingerEndMs = now + Math.max(0, lingerMs);
  }

  clearWorldEventPartnerOverlay(): void {
    this._heroMeetOverlay = null;
    this._heroMeetLingerEndMs = 0;
    this._meetBubbleSelf = null;
    this._meetBubbleOther = null;
  }

  /**
   * Show a transient world-event NPC close to hero position.
   * Used by quest-alms so the merchant is visible in-world.
   */
  setWorldEventNPCOverlay(npc: WorldEventNPCInfo, approaching = false, sessionId?: string): void {
    const name = (npc.name ?? '').trim();
    if (!name) return;
    this._worldEventNPCOverlay = {
      id: Number.isFinite(npc.id) ? Number(npc.id) : -910001,
      name,
      ...(npc.nameKey ? { nameKey: npc.nameKey } : {}),
      type: this._worldEventNPCRoleToType(npc.role),
    };
    const hero = { x: this._heroDisplayX, y: this._heroDisplayY };
    this._worldEventNpcEntrance.show(sessionId ?? `${npc.id}:${npc.nameKey ?? name}`, approaching, hero,
      nearestRoadDirection(this._roadNetwork, hero), this.camera, getViewport(), performance.now(), this._roadNetwork);
  }

  clearWorldEventNPCOverlay(): void {
    this._worldEventNPCOverlay = null;
    this._worldEventNpcEntrance.clear();
  }

  /** Display a committed server thought, never a locally selected phrase. */
  showHeroThought(key: string, text: string, remainingMs = 9000, occurrenceId?: string, args?: Record<string, unknown>): void {
    if (remainingMs <= 0 || (occurrenceId && occurrenceId === this._thoughtBubble?.occurrenceId)) return;
    if (!text.trim() || this._heroMeetDialoguePhase() || this._gameState.phase === GamePhase.Fighting || this._gameState.phase === GamePhase.Dead) return;
    this._thoughtBubble = { key, text, args: args ? { ...args } : undefined, startMs: performance.now() - (9000 - Math.min(9000, remainingMs)), occurrenceId };
  }

  showCrierAnnouncement(npcId: number, townId: number, text: string, remainingMs = 12000): boolean {
    if (!text.trim() || remainingMs <= 0 || !this._allNPCs.some(npc => npc.id === npcId && npc.townId === townId && npc.type === 'crier')) return false;
    this._crierBubble = { npcId, townId, text, startMs: performance.now() - (12000 - Math.min(12000, remainingMs)) };
    return true;
  }

  localizeNarrativeBubbles(thought: (key: string, args?: Record<string, unknown>) => string, crierText: string): void {
    if (this._thoughtBubble) this._thoughtBubble.text = thought(this._thoughtBubble.key, this._thoughtBubble.args);
    if (this._crierBubble) this._crierBubble.text = crierText;
  }

  applyWorldEventChatLine(
    fromHeroId: number,
    kind: 'player' | 'scripted',
    displayText: string,
  ): void {
    const myId = this._gameState.hero?.id;
    if (!myId || !displayText.trim()) return;
    const entry = {
      text: displayText,
      startMs: performance.now(),
      player: kind === 'player',
      ownPlayerMessage: kind === 'player' && fromHeroId === myId,
    };
    if (fromHeroId === myId) {
      this._meetBubbleSelf = entry;
    } else {
      this._meetBubbleOther = entry;
    }
  }

  private _initNearbyHeroInterp(heroId: number, x: number, y: number): void {
    this._nearbyHeroInterp.set(heroId, {
      displayX: x,
      displayY: y,
      prevX: x,
      prevY: y,
      targetX: x,
      targetY: y,
      lastUpdateTime: performance.now(),
      movingUntilMs: 0,
    });
  }

  private _applyNearbyHeroInterpStep(heroId: number, x: number, y: number): void {
    const interp = this._nearbyHeroInterp.get(heroId);
    if (!interp) {
      this._initNearbyHeroInterp(heroId, x, y);
      return;
    }
    if (interp.targetX === x && interp.targetY === y) return;
    interp.prevX = interp.displayX;
    interp.prevY = interp.displayY;
    interp.targetX = x;
    interp.targetY = y;
    interp.lastUpdateTime = performance.now();
  }

  private _nearbyHeroDisplayPos(
    heroId: number,
    x: number,
    y: number,
  ): { x: number; y: number } {
    const interp = this._nearbyHeroInterp.get(heroId);
    if (!interp) return { x, y };
    return { x: interp.displayX, y: interp.displayY };
  }

  private _nearbyHeroesForRender(): NearbyHeroPresentation[] {
    const now = performance.now();
    const heroes = this._nearbyHeroes.map((h) => {
      const pos = this._nearbyHeroDisplayPos(h.id, h.positionX, h.positionY);
      const interp = this._nearbyHeroInterp.get(h.id);
      const isMoving = Boolean(interp && now < interp.movingUntilMs);
      return { ...h, positionX: pos.x, positionY: pos.y, isMoving };
    });
    return this._nearbyAnimations.sample(heroes, now);
  }

  private _mergedNearbyHeroes(now: number): NearbyHeroPresentation[] {
    if (this._heroMeetLingerEndMs > 0 && now > this._heroMeetLingerEndMs) {
      this._heroMeetOverlay = null;
      this._heroMeetLingerEndMs = 0;
    }
    return this._nearbyHeroesForRender();
  }

  private _nearbyHeroesVisible(
    heroes: NearbyHeroData[],
    camX: number,
    camY: number,
    screenWidth: number,
    screenHeight: number,
  ): NearbyHeroData[] {
    return heroes.filter((h) =>
      isWorldPointInViewport(
        h.positionX,
        h.positionY,
        camX,
        camY,
        screenWidth,
        screenHeight,
        1.5, this.camera.zoom,
      ),
    );
  }

  private _worldEventNPCRoleToType(role: string | undefined): NPCData['type'] {
    const normalized = (role ?? '').trim().toLowerCase();
    if (normalized.includes('merchant')) return 'merchant';
    if (normalized.includes('healer')) return 'healer';
    if (normalized.includes('elder')) return 'elder';
    return 'quest_giver';
  }

  private _worldEventNPCForFrame(): NPCData | null {
    const overlay = this._worldEventNPCOverlay;
    if (!overlay) return null;
    const motion = this._worldEventNpcEntrance.sample(performance.now());
    if (!motion) return null;
    return {
      id: overlay.id,
      name: overlay.name,
      ...(overlay.nameKey ? { nameKey: overlay.nameKey } : {}),
      type: overlay.type,
      isMoving: motion.moving,
      presentationMotion: { dx: motion.dx, dy: motion.dy, elapsedMs: motion.elapsedMs, clip: motion.moving ? 'walk' : 'idle' },
      worldX: motion.x,
      worldY: motion.y,
      townId: 0,
      townLevelMin: 1,
      townLevelMax: 200,
    };
  }

  private _heroMeetBubbleTTLms = 9000;

  /** True only during hero_meet dialogue (server phase "meet"), not approach/return walks. */
  private _heroMeetDialoguePhase(): boolean {
    const h = this._gameState.hero;
    return (
      h?.excursionKind === 'hero_meet' &&
      h.excursionPhase?.toLowerCase() === 'meet'
    );
  }

  private _pruneMeetBubbles(now: number): void {
    const ttl = this._heroMeetBubbleTTLms;
    if (this._meetBubbleSelf && now - this._meetBubbleSelf.startMs > ttl) {
      this._meetBubbleSelf = null;
    }
    if (this._meetBubbleOther && now - this._meetBubbleOther.startMs > ttl) {
      this._meetBubbleOther = null;
    }
  }

  // ---- Server State Application ----

  /**
   * Initialize the engine with hero data from the REST init endpoint.
   * This sets the initial state before WebSocket messages start arriving.
   */
  initFromServer(hero: HeroState, serverHeroState?: string): void {
    this._thoughtBubble = null;
    this._crierBubble = null;
    this._overviewTownId = null;
    this._cameraTownId = null;
    this._heroStopFacing=undefined;
    this._heroAnimation.reset();
    this._movement.reset();
    this._applyState({ type: 'init', hero, activity: serverHeroState }, false);
    this._heroDisplayX = hero.position.x || 0;
    this._heroDisplayY = hero.position.y || 0;
    this._prevPositionX = this._heroDisplayX; this._prevPositionY = this._heroDisplayY;
    this._targetPositionX = this._heroDisplayX; this._targetPositionY = this._heroDisplayY;
    this._lastMoveUpdateTime = performance.now(); this._lootTimerMs = 0;
    const screen = worldToScreen(this._heroDisplayX, this._heroDisplayY);
    this.camera.setTarget(screen.x, screen.y); this.camera.snapToTarget();
    this._notifyStateChange();
  }

  /**
   * Accept server-time path samples; legacy snapshots retain the compatible lerp fallback.
   */
  applyHeroMove(
    x: number, y: number, targetX: number, targetY: number, speed: number,
    movement?: MovementPlan, stopFacing?: {x:number;y:number},
  ): void {
    if (this._destroyed || ![x, y, targetX, targetY, speed].every(Number.isFinite)) return;
    if (movement) {
      const first = movement.points?.[0];
      if (!first || Math.hypot(first.x - x, first.y - y) > 0.001) return;
      const result = this._movement.accept(movement, speed, this._gameState.hero?.id ?? -1, performance.now());
      if (!result.accepted) return;
      if (result.correction) {
        const point = this._movement.sample(performance.now());
        if (point) this._snapPresentation(point.x, point.y);
      }
    } else {
      // A legacy packet must not erase a newer clocked trajectory.
      if (this._movement.active) return;
      const meeting = this._heroMeetDialoguePhase();
      if (meeting) this._snapPresentation(x, y);
      this._prevPositionX = this._heroDisplayX; this._prevPositionY = this._heroDisplayY;
      this._lastMoveUpdateTime = performance.now();
    }
    this._heroStopFacing=stopFacing&&[stopFacing.x,stopFacing.y].every(Number.isFinite)?{...stopFacing}:undefined;
    this._targetPositionX = x; this._targetPositionY = y;
    this._moveTargetX = targetX; this._moveTargetY = targetY; this._heroSpeed = speed;
    this._applyState({ type: 'hero_move', x, y, preservePhase: this._heroMeetDialoguePhase() }, false);
  }

  /** Modern sync uses the same sample/order; never compare delayed display with current server state. */
  applyPositionSync(x: number, y: number, state: string, movement?: MovementPlan, speed?: number): void {
    if (this._destroyed || ![x, y].every(Number.isFinite)) return;
    if (movement) {
      this.applyHeroMove(x, y, x, y, speed ?? this._heroSpeed, movement);
      return;
    }
    if (this._movement.active) return;
    const drift = Math.hypot(this._heroDisplayX - x, this._heroDisplayY - y);
    if (drift > POSITION_DRIFT_SNAP_THRESHOLD) this._snapPresentation(x, y);
    this._applyState({ type: 'position_sync', x, y, activity: state }, false);
  }

  /** Corrections never contribute walking distance; automatic cameras follow the corrected point. */
  private _snapPresentation(x: number, y: number): void {
    this._heroAnimation.reset();
    this._heroDisplayX = x; this._heroDisplayY = y;
    this._prevPositionX = x; this._prevPositionY = y;
    this._targetPositionX = x; this._targetPositionY = y;
    this._lastMoveUpdateTime = performance.now();
    const point = worldToScreen(x, y);
    if (this._cameraFollow && this._overviewTownId === null) {
      this.camera.setTarget(point.x, point.y); this.camera.snapToTarget();
    }
  }

  private _stopPresentation(): Readonly<{ x: number; y: number }> {
    const stop = this._movement.stopAtLatestBaseline(performance.now());
    if (stop) {
      // Finish the bounded delayed tail at its authored speed, even when combat has begun.
      if (stop.correction) {
        const point = this._movement.sample(performance.now())!;
        this._snapPresentation(point.x, point.y);
      }
      this._interpolatePosition();
      return stop.point;
    }
    const point = this._gameState.hero?.position ?? { x: this._heroDisplayX, y: this._heroDisplayY };
    if (this._movement.active) this._movement.hold(point);
    else this._movement.reset();
    this._snapPresentation(point.x, point.y);
    return point;
  }

  /**
   * Called when server sends route_assigned.
   * Stores waypoints for optional path rendering.
   */
  applyRouteAssigned(
    waypoints: Array<{ x: number; y: number }>,
    speed: number,
  ): void {
    this._routeWaypoints = waypoints.map((point) => ({ ...point }));
    this._heroSpeed = speed; this._syncWorldTerrainContext();
    this._applyState({ type: 'route', waypoints });
  }

  /** Rebuild procedural terrain context from towns + current route (ring + active polyline). */
  private _syncWorldTerrainContext(): void {
    const context = townTerrainContext(
      this._towns, this._routeWaypoints.length >= 2 ? this._routeWaypoints : null, this._roadNetwork,
    );
    this.renderer.setWorldTerrainContext({ ...context, authoritativeScenery: this._sceneryChunks });
  }

  /** Visible camera chunks are requested by the same frame owner that renders them. */
  private _updateScenery(now: number): void {
    if (!this._authoritativeScenery || this._destroyed || !this._gameState.hero) return;
    const viewport = getViewport(), zoom = Math.max(this.camera.zoom, 0.01);
    const halfW = viewport.width / (2 * zoom) + 768, halfH = viewport.height / (2 * zoom) + 768;
    const corners = [screenToWorld(this.camera.finalX-halfW,this.camera.finalY-halfH),
      screenToWorld(this.camera.finalX+halfW,this.camera.finalY-halfH),
      screenToWorld(this.camera.finalX-halfW,this.camera.finalY+halfH),
      screenToWorld(this.camera.finalX+halfW,this.camera.finalY+halfH)];
    this._scenery.update({minX:Math.min(...corners.map(p=>p.x)),minY:Math.min(...corners.map(p=>p.y)),
      maxX:Math.max(...corners.map(p=>p.x)),maxY:Math.max(...corners.map(p=>p.y))},now);
  }

  /**
   * Apply a full hero state snapshot from the server.
   * Sent on WS connect, after level-up, revive, equipment change.
   */
  /** Fallback duration for debuff UI when `debuff_applied` lacks numeric durationMs. */
  getDebuffDurationMs(type: DebuffType): number | undefined {
    return this._debuffDurationMsByType[type];
  }

  applyHeroState(hero: HeroState): void {
    const previous = this._gameState;
    const identityChanged = previous.hero?.id !== hero.id;
    if (identityChanged) {this._movement.reset();this._heroStopFacing=undefined;}
    if (hero.debuffCatalogDurations) {
      this._debuffDurationMsByType = { ...this._debuffDurationMsByType, ...hero.debuffCatalogDurations };
    }
    this._cameraTownId = hero.currentTownId ?? null;
    this._applyState({ type: 'hero_snapshot', hero }, false);
    const continuingTownRest = !this._movement.active && isTownRestIdle(previous) && isTownRestIdle(this._gameState)
      && previous.phase === this._gameState.phase && previous.hero?.id === hero.id
      && previous.hero.modelVariant === hero.modelVariant && previous.hero.currentTownId === hero.currentTownId
      && previous.hero.position.x === hero.position.x && previous.hero.position.y === hero.position.y;
    if (!continuingTownRest && (!this._movement.active || previous.phase === GamePhase.Fighting)) this._heroAnimation.reset();
    const stopped = hero.hp <= 0 || (!this._movement.active && hero.serverActivityState === 'resting') ||
      (hero.excursionKind === 'hero_meet' && hero.excursionPhase?.toLowerCase() === 'meet');
    if ((identityChanged || stopped) && !continuingTownRest) this._stopPresentation();
    // Frequent ordinary snapshots update domain state, preserving the trajectory and stride phase.
    this._notifyStateChange();
  }

  /**
   * Called when server sends combat_start.
   */
  applyCombatStart(enemy: EnemyState, enemyPresentationPosition?: Readonly<{ x: number; y: number }>): void {
    this._thoughtBubble = null;
    const anchor = this._stopPresentation();
    // Optional fixture placement affects presentation only; copy it without editing incoming objects.
    const position = enemyPresentationPosition ? { ...enemyPresentationPosition }
      : { x: anchor.x + 1.5, y: anchor.y - 0.5 };
    this._lootTimerMs = 0;
    this._applyState({ type: 'combat_start', enemy: { ...enemy, position }, enemyOnScreenRight: position.x >= this._heroDisplayX });
  }

  /**
   * Called when server sends attack.
   * Updates HP values.
   */
  applyAttack(
    source: 'hero' | 'enemy' | 'potion' | 'dot' | 'summon',
    damage: number,
    isCrit: boolean,
    heroHp: number,
    enemyHp: number,
    outcome?: 'hit' | 'dodge' | 'block' | 'stun' | 'heal',
  ): void {
    void damage; void isCrit;
    if (!this._destroyed && this._gameState.phase === GamePhase.Fighting && this._gameState.enemy && this._gameState.hero && heroHp > 0) {
      if (source === 'hero') this._heroAnimation.attack(performance.now());
      else if (source === 'enemy' && (outcome === 'block' || outcome === 'dodge')) this._heroAnimation.defend(outcome, performance.now());
    }
    this._applyState({ type: 'attack', heroHp, enemyHp });
  }

  /**
   * Called when server sends enemy_regen.
   * Updates enemy HP.
   */
  applyEnemyRegen(amount: number, enemyHp: number): void {
    void amount;
    if (!this._gameState.enemy) return;
    this._applyState({ type: 'enemy_regen', hp: enemyHp });
  }

  /**
   * Called when server sends combat_end.
   * Transitions back to walking phase.
   */
  applyCombatEnd(): void { this._heroAnimation.reset(); this._applyState({ type: 'combat_end' }); }

  /**
   * Apply loot from combat_end for the popup UI.
   */
  applyLoot(loot: LootDrop | null): void { this._lootTimerMs = 0; this._applyState({ type: 'loot', loot }); }

  /**
   * Called when server sends hero_died.
   */
  applyHeroDied(): void { this._stopPresentation(); this._applyState({ type: 'died' }); }

  /**
   * Called when server sends hero_revived.
   */
  applyHeroRevived(hp: number): void { this._stopPresentation(); this._applyState({ type: 'revived', hp }); }

  /**
   * Called when server sends town_enter.
   * If buildings are provided, merge them into the matching town for rendering.
   */
  applyTownEnter(townId?: number, buildings?: BuildingData[]): void {
    this._cameraTownId = townId ?? this._gameState.hero?.currentTownId ?? null;
    this._cameraGeometryRevision++;
    if (townId && buildings?.length) {
      this._towns = this._towns.map((town) => town.id === townId && !town.authored ? { ...town, buildings: structuredClone(buildings) } : town);
    }
    this._applyState({ type: 'town_enter' });
  }

  /** Server simulated approach to a town NPC (quest / shop / healer). Narration uses adventure_log_line. */
  applyTownNPCVisit(_npcName: string, _npcType: string): void {
    void _npcName;
    void _npcType;
  }

  /**
   * Called when server sends town_exit.
   */
  applyTownExit(): void {
    this._cameraTownId = null;
    this._overviewTownId = null;
    this._applyState({ type: 'town_exit' });
    this.camera.zoom = clampGameZoom(this.camera.zoom);
    this._updateCameraTarget();
    this.camera.snapToTarget();
    this._publishCameraControls();
  }

  // ---- Patch helpers for UI-driven optimistic updates ----

  /** Merge server-side active buffs into the current hero. */
  patchHeroBuffs(activeBuffs: HeroState['activeBuffs']): void { if (this._gameState.hero) this._applyState({ type: 'hero_patch', fields: { activeBuffs } }); }

  /** Merge buff quota fields. */
  patchHeroBuffQuota(
    fields: Pick<
      HeroState,
      'buffFreeChargesRemaining' | 'buffQuotaPeriodEnd' | 'buffCharges'
    >,
  ): void { if (this._gameState.hero) this._applyState({ type: 'hero_patch', fields }); }

  /** Patch server-derived combat stats after buff application. */
  patchHeroCombat(
    combat: Pick<
      HeroState,
      'damage' | 'defense' | 'attackSpeed' | 'activeBuffs'
    >,
  ): void { if (this._gameState.hero) this._applyState({ type: 'hero_patch', fields: combat }); }

  /** Patch hero HP (e.g. after potion use). */
  patchHeroHp(hp: number): void { if (this._gameState.hero) this._applyState({ type: 'hero_hp', hp }); }

  /** Apply or refresh a single debuff from WS. */
  applyDebuffApplied(type: DebuffType, durationMs: number, expiresAtMs?: number): void {
    if (!this._gameState.hero) return;
    this._applyState({ type: 'debuff', debuffType: type, durationMs, expiresAtMs, nowMs: Date.now() });
  }

  // ---- Lifecycle ----

  /** Initialize the engine and attach to the DOM */
  async init(canvasContainer: HTMLElement): Promise<void> {
    if (this._destroyed) return;
    await this.renderer.init(canvasContainer);
    if (this._destroyed) return;
    this._syncWorldTerrainContext();

    this._handleResize = (): void => {
      this.renderer.resize();
    };
    window.addEventListener('resize', this._handleResize);
    window.addEventListener('orientationchange', this._handleResize);
  }

  /** Start the game loop */
  start(): void {
    if (this._running || this._destroyed) return;
    this._running = true;
    this._lastTime = performance.now();
    this._tick(performance.now());
  }

  /** Stop the game loop */
  stop(): void {
    this._running = false;
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  /** Clean up everything */
  destroy(): void {
    this._destroyed = true;
    this._scenery.destroy();
    this._nearbyAnimations.reset();
    this._movement.reset();
    this._thoughtBubble = null;
    this._crierBubble = null;
    this._heroAnimation.reset();
    this._onStateChange = null;
    this._cameraControlsListeners.clear();
    this.stop();
    if (this._handleResize) {
      window.removeEventListener('resize', this._handleResize);
      window.removeEventListener('orientationchange', this._handleResize);
      this._handleResize = null;
    }
    this.renderer.destroy();
  }

  // ---- Private: Loop ----

  /** Main loop tick - called by requestAnimationFrame */
  private _tick = (now: number): void => {
    if (!this._running) return;

    if (now - this._lastTime > 3000) {
      // Resume skips elapsed presentation rather than animating a correction as a long stride.
      const point = this._movement.sample(now);
      if (point) this._snapPresentation(point.x, point.y);
      else this._stopPresentation();
    }
    const frameTime = Math.min(now - this._lastTime, MAX_ACCUMULATED_MS);
    this._lastTime = now;

    // Interpolation + camera must run every frame. A 100ms fixed step (server tick rate)
    // only updated ~10×/s and made the view stutter on 60 Hz displays.
    if (frameTime > 0) {
      this._update(frameTime);
    }

    this._updateScenery(now);
    this._render();

    // Asset recovery may dispose the session synchronously from renderFrame.
    if (this._running && !this._destroyed) this._rafId = requestAnimationFrame(this._tick);
  };

  /** Per-frame update -- hero interpolation, camera follow, loot timer */
  private _update(dtMs: number): void {
    // Interpolate hero display position toward target
    this._interpolatePosition();
    this._interpolateNearbyHeroes();

    this._updateCameraTarget();
    this.camera.update(dtMs);
    this._publishCameraControls();

    // Auto-clear loot popup
    if (this._gameState.loot) {
      this._lootTimerMs += dtMs;
      if (this._lootTimerMs >= 2000) {
        this._applyState({ type: 'clear_loot' }, false);
        this._lootTimerMs = 0;
      }
    }
  }

  /**
   * Interpolate hero display position between server updates.
   *
   * Modern movement samples the bounded authoritative polyline on a shared monotonic clock.
   * Legacy packets interpolate the last coordinate over the historical 500ms window.
   */
  private _interpolatePosition(): void {
    const { point, correction } = this._movement.sampleFrame(performance.now());
    if (point) {
      if (correction) this._snapPresentation(point.x, point.y);
      this._heroDisplayX = point.x; this._heroDisplayY = point.y;
      return;
    }
    if (this._heroMeetDialoguePhase()) {
      this._heroDisplayX = this._targetPositionX;
      this._heroDisplayY = this._targetPositionY;
      this._prevPositionX = this._targetPositionX;
      this._prevPositionY = this._targetPositionY;
      return;
    }
    const elapsed =
      (performance.now() - this._lastMoveUpdateTime) / 1000;
    const t = Math.min(elapsed / MOVE_UPDATE_INTERVAL_S, 1.0);

    this._heroDisplayX =
      this._prevPositionX +
      (this._targetPositionX - this._prevPositionX) * t;
    this._heroDisplayY =
      this._prevPositionY +
      (this._targetPositionY - this._prevPositionY) * t;
  }

  /** Interpolate nearby hero sprites between server updates (same 2 Hz window). */
  private _interpolateNearbyHeroes(): void {
    const now = performance.now();
    for (const interp of this._nearbyHeroInterp.values()) {
      const elapsed = (now - interp.lastUpdateTime) / 1000;
      const t = Math.min(elapsed / MOVE_UPDATE_INTERVAL_S, 1.0);
      interp.displayX = interp.prevX + (interp.targetX - interp.prevX) * t;
      interp.displayY = interp.prevY + (interp.targetY - interp.prevY) * t;
    }
  }

  /** Render frame */
  private _render(): void {
    if (!this.renderer.initialized) return;

    const viewport = getViewport();

    const now = performance.now();
    const eventNPC = this._worldEventNPCForFrame();
    const frameNPCs = eventNPC ? [...this._allNPCs, eventNPC] : this._allNPCs;
    const nearbyVisible = this._nearbyHeroesVisible(
      this._mergedNearbyHeroes(now), this.camera.finalX, this.camera.finalY,
      viewport.width, viewport.height,
    );
    this._pruneMeetBubbles(now);
    const bubbleItems: SpeechBubbleView[] = [];
    if (this._thoughtBubble && now - this._thoughtBubble.startMs >= 9000) this._thoughtBubble = null;
    if (this._crierBubble && now - this._crierBubble.startMs >= 12000) this._crierBubble = null;
    if (this._thoughtBubble && !this._meetBubbleSelf && !this._heroMeetDialoguePhase() && this._gameState.phase !== GamePhase.Fighting && this._gameState.phase !== GamePhase.Dead) {
      bubbleItems.push({ wx: this._heroDisplayX, wy: this._heroDisplayY, text: this._thoughtBubble.text, startMs: this._thoughtBubble.startMs, ownPlayerMessage: false });
    }
    if (this._crierBubble) {
      const npc = frameNPCs.find(npc => npc.id === this._crierBubble!.npcId && npc.townId === this._crierBubble!.townId);
      if (npc) bubbleItems.push({ wx: npc.worldX, wy: npc.worldY, text: this._crierBubble.text, startMs: this._crierBubble.startMs, ownPlayerMessage: false });
    }
    if (this._meetBubbleSelf) {
      bubbleItems.push({
        wx: this._heroDisplayX,
        wy: this._heroDisplayY,
        text: this._meetBubbleSelf.text,
        startMs: this._meetBubbleSelf.startMs,
        ownPlayerMessage: this._meetBubbleSelf.ownPlayerMessage,
      });
    }
    const meetOv = this._heroMeetOverlay;
    if (this._meetBubbleOther && meetOv) {
      const meetPos = this._nearbyHeroDisplayPos(
        meetOv.id,
        meetOv.positionX,
        meetOv.positionY,
      );
      bubbleItems.push({
        wx: meetPos.x,
        wy: meetPos.y,
        text: this._meetBubbleOther.text,
        startMs: this._meetBubbleOther.startMs,
        ownPlayerMessage: this._meetBubbleOther.ownPlayerMessage,
      });
    }
    this.renderer.renderFrame(createRenderSnapshot({ ...this._gameState, serverTimeMs: Math.max(this._gameState.serverTimeMs ?? 0, this._residentServerTimeMs) }, {
      nowMs: now, viewport, camera: this.camera,
      heroPosition: { x: this._heroDisplayX, y: this._heroDisplayY },
      heroMoving: this._movement.moving,
      heroMotion: this._heroAnimation.sample(now, this._heroDisplayX, this._heroDisplayY,
        this._gameState.phase === GamePhase.Fighting ? this._gameState.enemy?.position ?? null : null,
        this._gameState.phase === GamePhase.Dead || (this._gameState.hero?.hp ?? 0) <= 0, this._gameState.hero?.modelVariant,
        (this._gameState.phase !== GamePhase.Resting || isTownRestIdle(this._gameState)) && this._gameState.phase !== GamePhase.Fighting, this._heroStopFacing),
      towns: this._towns, npcs: frameNPCs, nearbyHeroes: nearbyVisible, bubbles: bubbleItems,
    }));
  }

  /** Immutable partial projection for REST responses; presentation position stays separate. */
  applyHeroPatch(fields: Partial<HeroState>): void {
    if (!this._destroyed) this._applyState({ type: 'hero_patch', fields });
  }

  // ---- Private: Helpers ----

  private _applyState(event: ServerStateEvent, notify = true): void {
    if (this._destroyed) return;
    this._gameState = applyServerStateEvent(this._gameState, event);
    if (notify) this._notifyStateChange();
  }

  private _notifyStateChange(): void {
    this._onStateChange?.(this._gameState);
  }
}
